import { Extension } from "@tiptap/core";
import { Suggestion } from "@tiptap/suggestion";
import { PluginKey } from "@tiptap/pm/state";
import tippy from "tippy.js";
import { fetchHealthHistoryPhrases } from "./health-clients-api";
import { pick, toNumber } from "./health-clients-utils";

const phrasePluginKey = new PluginKey("historyPhraseSuggestion");

const normalizePhrase = (row) => ({
  idText: toNumber(pick(row, "idText", "IdText")),
  name: pick(row, "name", "Name") ?? "",
  phrase: pick(row, "phrase", "Phrase") ?? ""
});

/**
 * Слово перед курсором (от 2 символов) или `/запрос`.
 */
function findPhraseMatch({ $position }) {
  const nodeBefore = $position.nodeBefore;
  if (!nodeBefore?.isText || !nodeBefore.text) return null;

  const text = nodeBefore.text;

  const slashMatch = /\/([\wа-яА-ЯёЁ-]*)$/.exec(text);
  if (slashMatch) {
    const token = slashMatch[0];
    const from = $position.pos - token.length;
    return {
      range: { from, to: $position.pos },
      query: slashMatch[1] || "",
      text: token
    };
  }

  const wordMatch = /[\wа-яА-ЯёЁ-]{2,}$/.exec(text);
  if (!wordMatch) return null;

  const word = wordMatch[0];
  const from = $position.pos - word.length;
  return {
    range: { from, to: $position.pos },
    query: word,
    text: word
  };
}

/**
 * Автоподстановка фраз при наборе (и по `/`).
 */
export const HistoryPhraseSuggestion = Extension.create({
  name: "historyPhraseSuggestion",

  addOptions() {
    return {
      suggestion: {
        char: "/",
        pluginKey: phrasePluginKey,
        allowSpaces: false,
        minQueryLength: 0,
        debounce: 250,
        allowedPrefixes: null,
        findSuggestionMatch: findPhraseMatch,
        items: async ({ query }) => {
          // Для режима слова — минимум 2 символа (уже в findPhraseMatch).
          // Для `/` без текста — покажем общий список.
          try {
            const rows = await fetchHealthHistoryPhrases(query || "");
            return (Array.isArray(rows) ? rows : [])
              .map(normalizePhrase)
              .filter((p) => p.phrase)
              .slice(0, 30);
          } catch {
            return [];
          }
        },
        command: ({ editor, range, props }) => {
          editor.chain().focus().deleteRange(range).insertContent(`${props.phrase} `).run();
        },
        render: () => {
          let component = null;
          let popup = null;

          const destroyPopup = () => {
            popup?.[0]?.destroy();
            popup = null;
            component = null;
          };

          const ensurePopup = (props) => {
            if (!props.clientRect) return false;
            if (!props.items?.length) {
              destroyPopup();
              return false;
            }

            if (!component) {
              component = document.createElement("div");
              component.className = "client-history-phrase-menu";
            }
            updateList(component, props);

            if (!popup) {
              popup = tippy("body", {
                getReferenceClientRect: props.clientRect,
                appendTo: () => document.body,
                content: component,
                showOnCreate: true,
                interactive: true,
                trigger: "manual",
                placement: "bottom-start",
                theme: "light-border"
              });
            } else {
              popup[0].setProps({ getReferenceClientRect: props.clientRect });
              popup[0].show();
            }
            return true;
          };

          return {
            onStart: (props) => {
              ensurePopup(props);
            },
            onUpdate: (props) => {
              ensurePopup(props);
            },
            onKeyDown: (props) => {
              if (props.event.key === "Escape") {
                destroyPopup();
                return true;
              }
              if (!popup) return false;
              return component?.onKeyDown?.(props.event) ?? false;
            },
            onExit: () => {
              destroyPopup();
            }
          };
        }
      }
    };
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion
      })
    ];
  }
});

function updateList(root, props) {
  if (!root) return;

  const items = props.items || [];
  let selected = 0;

  const render = () => {
    root.innerHTML = "";
    items.forEach((item, index) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className =
        "client-history-phrase-menu__item" +
        (index === selected ? " client-history-phrase-menu__item--active" : "");

      const label = document.createElement("span");
      label.textContent = item.phrase;
      btn.appendChild(label);

      if (item.name && item.name !== item.phrase) {
        const detail = document.createElement("span");
        detail.className = "client-history-phrase-menu__detail";
        detail.textContent = item.name;
        btn.appendChild(detail);
      }
      btn.onmousedown = (e) => e.preventDefault();
      btn.onclick = () => props.command(item);
      root.appendChild(btn);
    });
  };

  root.onKeyDown = (event) => {
    if (!items.length) return false;
    if (event.key === "ArrowDown") {
      selected = (selected + 1) % items.length;
      render();
      return true;
    }
    if (event.key === "ArrowUp") {
      selected = (selected - 1 + items.length) % items.length;
      render();
      return true;
    }
    if (event.key === "Enter") {
      props.command(items[selected]);
      return true;
    }
    return false;
  };

  render();
}
